import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Banner,
  Badge as PolarisBadge,
  Button as PolarisButton,
  Card,
  Checkbox,
  Spinner,
} from "@shopify/polaris";
import { API_URL } from "constants.js";
import UserSelect from "features/user-select/user-select";
import PrettyDate from "components/format/PrettyDate";
import { PHONE_PAGE_GUTTER, PHONE_PAGE_MARGIN } from "components/tw/pageGutter";
import { detectBrowser } from "features/calls/browser-help";
import {
  fetchUserSignedDocuments,
  UserSignedDocument,
} from "services/work-security.service";
import { WorkSecurityArea, WorkSecurityAreaScopeProps } from "services/work-security-areas.service";

type DownloadMode = "selected" | "all" | null;

interface Props extends WorkSecurityAreaScopeProps {
  /** Accessible areas (from the module header's useAreaScope) — used to label the "Sritis" badge. */
  areas?: WorkSecurityArea[];
  /**
   * Fixed employee (the «darbuotojo kortelė» Tvarkos tab): hides the user
   * picker and shows this person's documents only.
   */
  userId?: string;
}

/** Falls back to a generic label for a document whose area was since archived (excluded from `areas`). */
function areaLabel(areas: WorkSecurityArea[], t: any, areaId?: string | null): string {
  const found = areaId ? areas.find((a) => a.id === areaId) : undefined;
  return found ? found.name : t("area_unknown", { defaultValue: "Other sphere" });
}

/**
 * Admin tab: pick a user and browse / download their signed work-security docs.
 *
 * `fetchUserSignedDocuments` has no server-side area filter (verified against
 * the backend controller — see the NOTE on it in work-security.service.ts),
 * so narrowing by `areaId` and labelling rows in "all areas" mode both happen
 * client-side here, off the `areaId` every document already carries.
 */
export default function WorkSecuritySignedDocumentsPanel({
  areaId,
  selectedArea,
  areas = [],
  userId: fixedUserId,
}: Props = {}) {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  // "All areas" mode, and only worth labelling when there is more than one
  // sphere to disambiguate — matches the module header's own switcher gate.
  const showAreaLabel = !areaId && areas.length > 1;

  const [pickedUserId, setUserId] = useState("");
  const userId = fixedUserId || pickedUserId;
  const [documents, setDocuments] = useState<UserSignedDocument[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [downloadMode, setDownloadMode] = useState<DownloadMode>(null);
  const documentsRequestRef = useRef(0);

  // A concrete sphere selection narrows the (unfiltered-by-the-backend) list
  // to just that sphere's documents — see the module-level NOTE above.
  const visibleDocuments = useMemo(
    () => (areaId ? documents.filter((doc) => doc.areaId === areaId) : documents),
    [documents, areaId]
  );

  const loadDocuments = useCallback(async () => {
    const requestId = ++documentsRequestRef.current;
    if (!companyId || !userId) {
      setDocuments([]);
      setSelectedIds(new Set());
      setLoadError(false);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setLoadError(false);
      const data = await fetchUserSignedDocuments(companyId, userId);
      if (requestId !== documentsRequestRef.current) return;
      setDocuments(data.documents || []);
      setSelectedIds(new Set());
    } catch (err) {
      if (requestId !== documentsRequestRef.current) return;
      console.error("Failed to load user signed work security documents", err);
      setDocuments([]);
      setLoadError(true);
    } finally {
      if (requestId === documentsRequestRef.current) {
        setLoading(false);
      }
    }
  }, [companyId, userId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const toggleSelected = (docId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(docId);
      else next.delete(docId);
      return next;
    });
  };

  const allSelected =
    visibleDocuments.length > 0 && selectedIds.size === visibleDocuments.length;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(visibleDocuments.map((d) => d.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const getDownloadUrl = (doc: UserSignedDocument) => {
    if (doc.signatureId) {
      return `${API_URL}/${companyId}/files/download/${doc.fileId}/signature/${doc.signatureId}`;
    }
    return `${API_URL}/${companyId}/files/download/${doc.fileId}`;
  };

  const downloadDocs = async (docs: UserSignedDocument[], mode: Exclude<DownloadMode, null>) => {
    if (!docs.length) return;
    setDownloadMode(mode);
    try {
      for (const doc of docs) {
        const response = await axios.get(getDownloadUrl(doc), { responseType: "blob" });
        const objectUrl = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = doc.fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      }
    } finally {
      setDownloadMode(null);
    }
  };

  // iOS Safari asks about every file after the first in a download loop, or drops them, so there only
  // each row's own Download is offered, and the checkboxes that fed the bulk buttons go with them.
  const canDownloadInBulk = detectBrowser().os !== "ios";

  const documentText = (doc: UserSignedDocument) => (
    <>
      <span className="block truncate text-sm font-medium text-zinc-900 dark:text-white">
        {doc.fileName}
      </span>
      <span className="mt-0.5 block text-xs text-zinc-500">
        {doc.roleName}
        {doc.signedAt ? (
          <>
            {" · "}
            {t("signed_on", { defaultValue: "Signed on" })}{" "}
            <PrettyDate dateString={doc.signedAt} />
          </>
        ) : null}
        {doc.isClosed ? (
          <>
            {" · "}
            {t("document_outdated", { defaultValue: "Outdated" })}
          </>
        ) : null}
      </span>
    </>
  );

  const selectedDocuments = visibleDocuments.filter((d) => selectedIds.has(d.id));
  const isDownloading = downloadMode !== null;

  return (
    <div className={fixedUserId ? "space-y-4" : "space-y-4 mt-4"}>
      {!fixedUserId && (
        <Card>
          <div className="p-2 max-w-xl">
            <UserSelect
              value={pickedUserId}
              onChange={setUserId}
              label="select_user"
              showUserEmail
              onlyActivated={false}
            />
          </div>
        </Card>
      )}

      {!userId && (
        <p className={`text-sm text-zinc-500 italic py-6 text-center ${PHONE_PAGE_GUTTER}`}>
          {t("select_user_to_view_signed_docs", {
            defaultValue: "Select a user to view their signed procedure documents.",
          })}
        </p>
      )}

      {userId && loadError && (
        <div className="space-y-3">
          <Banner tone="critical">
            {t("signed_documents_load_failed", {
              defaultValue: "Unable to load signed documents. Please try again.",
            })}
          </Banner>
          <div className="flex justify-center">
            <PolarisButton onClick={loadDocuments} disabled={loading}>
              {t("retry", { defaultValue: "Retry" })}
            </PolarisButton>
          </div>
        </div>
      )}

      {userId && !loadError && (
        <div className={`bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-800 ${PHONE_PAGE_MARGIN}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
            <div className="flex items-center gap-3">
              {canDownloadInBulk && (
                <Checkbox
                  label={t("select_all", { defaultValue: "Select all" })}
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  disabled={loading || visibleDocuments.length === 0}
                />
              )}
              <span className="text-sm text-zinc-500">
                {visibleDocuments.length}{" "}
                {t("signed_documents", { defaultValue: "Signed Documents" }).toLowerCase()}
              </span>
            </div>
            {canDownloadInBulk && (
              <div className="flex flex-wrap items-center gap-2">
                <PolarisButton
                  onClick={() => downloadDocs(selectedDocuments, "selected")}
                  disabled={isDownloading || selectedDocuments.length === 0}
                  loading={downloadMode === "selected"}
                >
                  {t("download_selected", {
                    defaultValue: "Download selected",
                  })}
                </PolarisButton>
                <PolarisButton
                  variant="primary"
                  onClick={() => downloadDocs(visibleDocuments, "all")}
                  disabled={isDownloading || visibleDocuments.length === 0}
                  loading={downloadMode === "all"}
                >
                  {t("download_all", { defaultValue: "Download all" })}
                </PolarisButton>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner size="large" />
            </div>
          ) : visibleDocuments.length === 0 ? (
            <p className="text-sm text-zinc-400 italic py-10 text-center">
              {t("no_signed_documents_for_user", {
                defaultValue: "This user has no signed procedure documents.",
              })}
            </p>
          ) : (
            <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {visibleDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/40"
                >
                  {canDownloadInBulk && (
                    <Checkbox
                      id={`signed-doc-${doc.id}`}
                      label=""
                      labelHidden
                      checked={selectedIds.has(doc.id)}
                      onChange={(checked) => toggleSelected(doc.id, checked)}
                    />
                  )}
                  {/* The whole text block ticks the checkbox, so a thumb need not hit its 20 px box. */}
                  {canDownloadInBulk ? (
                    <label
                      htmlFor={`signed-doc-${doc.id}`}
                      className="min-w-0 flex-1 cursor-pointer"
                    >
                      {documentText(doc)}
                    </label>
                  ) : (
                    <div className="min-w-0 flex-1">{documentText(doc)}</div>
                  )}
                  {showAreaLabel && (
                    <PolarisBadge>{areaLabel(areas, t, doc.areaId)}</PolarisBadge>
                  )}
                  <PolarisButton
                    variant="plain"
                    onClick={() =>
                      window.open(getDownloadUrl(doc), "_blank", "noopener,noreferrer")
                    }
                    disabled={isDownloading}
                  >
                    {t("download", { defaultValue: "Download" })}
                  </PolarisButton>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
